import emailjs from '@emailjs/browser';
import { Vehicle } from '../types/vehicle';
import { FuelConsumption } from '../types/fuelConsumption';
import { MaintenanceRecord } from '../types/maintenance';
import { db } from '../config/firebase';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getMessaging, getToken } from 'firebase/messaging';

const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
const FIREBASE_VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

/**
 * Capitalise correctement un nom (première lettre en majuscule, reste en minuscule)
 * Gère les noms composés avec tirets (ex: "jean-pierre" → "Jean-Pierre")
 */
function capitalizeFullName(name: string): string {
  if (!name) return '';

  return name
    .split(' ')
    .map(part =>
      part.split('-')
        .map(subPart => {
          if (!subPart) return '';
          return subPart.charAt(0).toUpperCase() + subPart.slice(1).toLowerCase();
        })
        .join('-')
    )
    .join(' ');
}

/**
 * Convertit une date (Timestamp Firestore, Date ou string) en Date valide
 */
function toValidDate(date: any): Date {
  if (!date) return new Date();

  // Si c'est déjà un objet Date
  if (date instanceof Date) {
    return date;
  }

  // Si c'est un Timestamp Firestore
  if (date?.toDate && typeof date.toDate === 'function') {
    return date.toDate();
  }

  // Si c'est un objet avec seconds (Timestamp format JSON)
  if (date?.seconds) {
    return new Date(date.seconds * 1000);
  }

  // Sinon, tenter de créer une Date
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

interface EmailParams extends Record<string, unknown> {
  to_email: string;
  to_name: string;
  subject: string;
  message: string;
}

interface NotificationPreferences {
  enabled: boolean;
  maintenance: boolean;
  fuel: boolean;
  mileage: boolean;
}

interface ExtendedNotificationOptions extends NotificationOptions {
  vibrate?: number[];
  tag?: string;
  renotify?: boolean;
}

export const notificationService = {
  // Méthodes existantes
  async requestNotificationPermission(): Promise<boolean> {
    try {
      if (!('Notification' in window)) {
        console.log('Ce navigateur ne supporte pas les notifications desktop');
        return false;
      }

      if (!('serviceWorker' in navigator)) {
        console.log('Ce navigateur ne supporte pas les Service Workers');
        return false;
      }

      console.log('Demande de permission pour les notifications');
      const permission = await Notification.requestPermission();
      console.log('Permission obtenue:', permission);

      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        console.log('Service Worker prêt pour les notifications:', registration);
        
        // Vérifier si le service worker est actif
        if (registration.active) {
          console.log('Service Worker actif:', registration.active.state);
        } else {
          console.log('Service Worker non actif');
        }
      }

      return permission === 'granted';
    } catch (error) {
      console.error('Erreur lors de la demande de permission:', error);
      return false;
    }
  },

  async initializeFirebaseMessaging(userId: string) {
    try {
      const messaging = getMessaging();
      console.log('Initialisation de Firebase Messaging');

      const permission = await this.requestNotificationPermission();
      if (!permission) {
        console.log('Permission de notification refusée');
        return null;
      }

      // Vérifier l'enregistrement du service worker
      const registration = await navigator.serviceWorker.ready;
      console.log('Service Worker prêt pour FCM:', registration);

      try {
        console.log('Tentative d\'obtention du token FCM avec VAPID key:', FIREBASE_VAPID_KEY?.substring(0, 10) + '...');
        const currentToken = await getToken(messaging, {
          vapidKey: FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration
        });

        if (currentToken) {
          console.log('Token FCM obtenu:', currentToken);
          
          // Tentative de sauvegarde du token, mais on continue même si ça échoue
          try {
            await this.saveFCMToken(userId, currentToken);
            console.log('Token FCM sauvegardé avec succès');
          } catch (saveError) {
            console.warn('Impossible de sauvegarder le token FCM, mais les notifications peuvent fonctionner quand même:', saveError);
          }
          
          return currentToken;
        } else {
          console.log('Impossible d\'obtenir le token');
          return null;
        }
      } catch (error) {
        console.error('Erreur lors de l\'obtention du token FCM:', error);
        console.error('Détails de l\'erreur:', error instanceof Error ? error.message : error);
        // En cas d'erreur avec FCM, on utilise les notifications natives
        return 'native';
      }
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de Firebase Messaging:', error);
      console.error('Détails de l\'erreur:', error instanceof Error ? error.message : error);
      // En cas d'erreur avec FCM, on utilise les notifications natives
      return 'native';
    }
  },

  async saveFCMToken(userId: string, token: string) {
    try {
      console.log('Sauvegarde du token FCM pour l\'utilisateur:', userId);
      
      // Vérifier si l'utilisateur existe
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        console.warn('Utilisateur non trouvé dans la collection users, tentative de création du document');
        // Créer le document utilisateur s'il n'existe pas
        try {
          await setDoc(userRef, {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            fcmTokens: [token]
          }, { merge: true });
        } catch (createError) {
          console.warn('Impossible de créer le document utilisateur:', createError);
          // Continuer malgré l'erreur
        }
      }
      
      // Utiliser le bon chemin de collection selon les règles de sécurité
      const tokenRef = doc(db, 'users', userId, 'tokens', token);
      const tokenData = {
        token: token,
        platform: 'web',
        createdAt: serverTimestamp()
      };
      console.log('Données du token à sauvegarder:', tokenData);
      
      try {
        // Ne pas utiliser merge: true pour s'assurer que createdAt est exactement égal à request.time
        await setDoc(tokenRef, tokenData);
        console.log('Token FCM correctement enregistré dans Firestore');
        return true;
      } catch (tokenError) {
        console.warn('Erreur lors de la sauvegarde du token FCM dans la sous-collection tokens:', tokenError);
        
        // Tentative alternative : sauvegarder le token directement dans le document utilisateur
        try {
          await updateDoc(userRef, {
            fcmToken: token,
            fcmTokenUpdatedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          console.log('Token FCM sauvegardé dans le document utilisateur principal');
          return true;
        } catch (updateError) {
          console.error('Échec de toutes les tentatives de sauvegarde du token FCM:', updateError);
          // Ne pas lancer d'exception, retourner false pour indiquer l'échec
          return false;
        }
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du token FCM:', error);
      if (error instanceof Error) {
        console.error('Détails de l\'erreur:', error.message);
        console.error('Stack trace:', error.stack);
      }
      // Ne pas lancer d'exception, retourner false pour indiquer l'échec
      return false;
    }
  },

  async saveNotificationPreferences(userId: string, preferences: NotificationPreferences) {
    try {
      console.log('Sauvegarde des préférences de notification pour l\'utilisateur:', userId);
      const userPrefsRef = doc(db, 'users', userId, 'settings', 'notifications');
      
      // S'assurer que tous les champs requis sont présents
      const prefsData = {
        enabled: preferences.enabled,
        maintenance: preferences.maintenance ?? true,
        fuel: preferences.fuel ?? true,
        mileage: preferences.mileage ?? true,
        updatedAt: new Date().toISOString()
      };
      
      console.log('Données des préférences à sauvegarder:', prefsData);
      await setDoc(userPrefsRef, prefsData);
      
      if (preferences.enabled) {
        console.log('Notifications activées, initialisation de Firebase Messaging');
        const result = await this.initializeFirebaseMessaging(userId);
        if (result === 'native') {
          console.log('Utilisation des notifications natives');
        } else if (result) {
          console.log('Firebase Messaging initialisé avec succès');
        }
      }
      
      console.log('Préférences sauvegardées avec succès');
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des préférences:', error);
      if (error instanceof Error) {
        console.error('Détails de l\'erreur:', error.message);
        console.error('Stack trace:', error.stack);
      }
      throw error;
    }
  },

  async getNotificationPreferences(userId: string): Promise<NotificationPreferences> {
    try {
      console.log('Récupération des préférences de notification pour l\'utilisateur:', userId);
      const userPrefsRef = doc(db, 'users', userId, 'settings', 'notifications');
      const prefsDoc = await getDoc(userPrefsRef);
      
      if (prefsDoc.exists()) {
        console.log('Préférences trouvées:', prefsDoc.data());
        return prefsDoc.data() as NotificationPreferences;
      }
      
      console.log('Aucune préférence trouvée, utilisation des valeurs par défaut');
      return {
        enabled: false,
        maintenance: true,
        fuel: true,
        mileage: true
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des préférences:', error);
      return {
        enabled: false,
        maintenance: true,
        fuel: true,
        mileage: true
      };
    }
  },

  async showNotification(title: string, options: ExtendedNotificationOptions) {
    try {
      if (!('Notification' in window)) {
        console.log('Ce navigateur ne supporte pas les notifications desktop');
        return false;
      }

      console.log('État actuel de la permission:', Notification.permission);

      if (Notification.permission === 'default') {
        console.log('Demande de permission pour les notifications');
        const permission = await Notification.requestPermission();
        console.log('Nouvelle permission:', permission);
        if (permission !== 'granted') {
          console.log('Permission refusée');
          return false;
        }
      }

      if (Notification.permission === 'granted') {
        console.log('Tentative d\'affichage de la notification:', { title, options });
        
        const registration = await navigator.serviceWorker.ready;
        if (!registration) {
          console.error('Service Worker non enregistré');
          return false;
        }
        
        console.log('Service Worker prêt, affichage de la notification');
        const notificationOptions = {
          ...options,
          icon: '/logo-verifleet1.png',
          badge: '/logo-verifleet1.png',
          requireInteraction: true,
          data: {
            ...(options.data || {}),
            vibrate: [200, 100, 200],
            tag: 'verifleet-notification',
            renotify: true
          }
        } as NotificationOptions;

        await registration.showNotification(title, notificationOptions);
        
        console.log('Notification affichée avec succès');
        return true;
      } else {
        console.log('Permission de notification non accordée:', Notification.permission);
        return false;
      }
    } catch (error) {
      console.error('Erreur lors de l\'affichage de la notification:', error);
      return false;
    }
  },

  async sendNotificationEmail(params: EmailParams) {
    try {
      console.log('EmailJS Configuration:', {
        serviceId: EMAILJS_SERVICE_ID,
        templateId: EMAILJS_TEMPLATE_ID,
        publicKey: EMAILJS_PUBLIC_KEY?.substring(0, 5) + '...'
      });
      
      console.log('Envoi de l\'email de notification avec les paramètres:', {
        to_email: params.to_email,
        to_name: params.to_name,
        subject: params.subject,
        message: params.message,
      });

      const templateParams = {
        to_email: params.to_email,
        to_name: capitalizeFullName(params.to_name),
        subject: params.subject,
        message: params.message,
        reply_to: params.to_email
      };

      console.log('Paramètres du template formatés:', templateParams);

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      console.log('Email envoyé avec succès:', response);
      return response;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
      throw error;
    }
  },

  formatInvitationMessage(organizationName: string, role: string, functionTitle: string, email: string) {
    return `
Vous avez été invité à rejoindre l'organisation ${organizationName}

Détails de l'invitation :
------------------------
Rôle : ${role}
Fonction : ${functionTitle}

Instructions :
-------------
1. Si vous n'avez pas encore de compte, créez-en un en cliquant sur ce lien : https://projet-1-auth-11ef8.web.app/signup
   Utilisez cette adresse email (${email}) lors de l'inscription.
2. Une fois inscrit, connectez-vous et accédez à la page "Invitations" depuis le menu ou en visitant : https://projet-1-auth-11ef8.web.app/invitations
3. Vous pourrez alors accepter l'invitation et rejoindre l'organisation

Note : Cette invitation expirera dans 7 jours.

L'équipe VeriFleet
    `;
  },

  formatMaintenanceMessage(maintenance: MaintenanceRecord, vehicle: Vehicle) {
    const openDate = toValidDate(maintenance.openDate);

    return `
      Nouvelle fiche maintenance

      Véhicule: ${vehicle.make} ${vehicle.model}
      Immatriculation: ${vehicle.licensePlate}
      Type: ${maintenance.type}
      Description: ${maintenance.description}
      Date: ${openDate.toLocaleDateString('fr-FR')}
      Coût TTC: ${maintenance.costTTC}€

      Prestataire: ${maintenance.serviceProviderId ? maintenance.serviceProviderId : 'Non spécifié'}
      Statut: ${maintenance.status}
    `;
  },

  formatFuelMessage(fuel: FuelConsumption, vehicle: Vehicle) {
    const fuelDate = toValidDate(fuel.date);

    return `
      Nouveau ticket carburant

      Véhicule: ${vehicle.make} ${vehicle.model}
      Immatriculation: ${vehicle.licensePlate}
      Montant: ${fuel.amount}€
      Litres: ${fuel.volume}L
      Prix/L: ${(fuel.amount / fuel.volume).toFixed(2)}€
      Date: ${fuelDate.toLocaleDateString('fr-FR')}
      Station: Non spécifiée
    `;
  },

  formatMileageMessage(mileage: any, vehicle: Vehicle) {
    const mileageDate = toValidDate(mileage.date);

    return `
      Nouveau relevé kilométrique

      Véhicule: ${vehicle.make} ${vehicle.model}
      Immatriculation: ${vehicle.licensePlate}
      Kilométrage: ${mileage.mileage} km
      Date: ${mileageDate.toLocaleDateString('fr-FR')}
      Commentaire: ${mileage.comment || 'Aucun'}
    `;
  },

  // Nouvelles méthodes pour la réservation
  formatReservationConfirmationMessage(
    reservation: any,
    vehicle: Vehicle,
    validationCode?: string
  ) {
    const startDate = toValidDate(reservation.startDate);
    const endDate = toValidDate(reservation.endDate);
    const validationInfo = validationCode
      ? `\nCode de validation: ${validationCode}\nCe code est valable pendant 24 heures.`
      : '';

    return `
      Confirmation de réservation

      Véhicule: ${vehicle.make} ${vehicle.model}
      Immatriculation: ${vehicle.licensePlate}
      Début: ${startDate.toLocaleString('fr-FR')}
      Fin: ${endDate.toLocaleString('fr-FR')}
      Statut: ${reservation.status}
      ${validationInfo}

      Notes: ${reservation.notes || 'Aucune'}
    `;
  },

  formatReservationReminderMessage(
    reservation: any,
    vehicle: Vehicle
  ) {
    const startDate = toValidDate(reservation.startDate);
    const endDate = toValidDate(reservation.endDate);

    return `
      Rappel de réservation

      Véhicule: ${vehicle.make} ${vehicle.model}
      Immatriculation: ${vehicle.licensePlate}
      Début: ${startDate.toLocaleString('fr-FR')}
      Fin: ${endDate.toLocaleString('fr-FR')}

      Notes: ${reservation.notes || 'Aucune'}
    `;
  },

  formatReservationCancellationMessage(
    reservation: any,
    vehicle: Vehicle
  ) {
    const startDate = toValidDate(reservation.startDate);
    const endDate = toValidDate(reservation.endDate);

    return `
      Annulation de réservation

      Véhicule: ${vehicle.make} ${vehicle.model}
      Immatriculation: ${vehicle.licensePlate}
      Début: ${startDate.toLocaleString('fr-FR')}
      Fin: ${endDate.toLocaleString('fr-FR')}

      Motif: ${reservation.cancellationReason || 'Non spécifié'}
    `;
  },

  formatNewReservationAdminMessage(
    reservation: any,
    vehicle: Vehicle,
    userEmail: string
  ) {
    const startDate = toValidDate(reservation.startDate);
    const endDate = toValidDate(reservation.endDate);

    return `
      Nouvelle réservation

      Véhicule: ${vehicle.make} ${vehicle.model}
      Immatriculation: ${vehicle.licensePlate}
      Utilisateur: ${userEmail}
      Début: ${startDate.toLocaleString('fr-FR')}
      Fin: ${endDate.toLocaleString('fr-FR')}
      Statut: ${reservation.status}

      Notes: ${reservation.notes || 'Aucune'}
    `;
  },

  async notifyAdminsAboutNewReservation(
    organizationId: string,
    reservationId: string,
    vehicleId: string,
    userEmail: string
  ): Promise<void> {
    try {
      console.log('Envoi de notifications aux administrateurs pour la nouvelle réservation:', reservationId);
      
      // Importer dynamiquement pour éviter les dépendances circulaires
      const { organizationService } = await import('./organizationService');
      const { vehicleService } = await import('./vehicleService');
      const { reservationService } = await import('./reservationService');
      
      // Récupérer les détails de la réservation
      const reservation = await reservationService.getReservation(reservationId);
      if (!reservation) {
        console.error('Réservation non trouvée:', reservationId);
        return;
      }
      
      // Récupérer les détails du véhicule
      const vehicle = await vehicleService.getVehicle(vehicleId);
      if (!vehicle) {
        console.error('Véhicule non trouvé:', vehicleId);
        return;
      }
      
      // Récupérer les administrateurs de l'organisation
      const admins = await organizationService.getOrganizationAdmins(organizationId);
      if (!admins || admins.length === 0) {
        console.log('Aucun administrateur trouvé pour l\'organisation:', organizationId);
        return;
      }
      
      console.log('Administrateurs trouvés:', admins.length);
      
      // Préparer le message de notification
      const title = 'Nouvelle réservation';
      const message = this.formatNewReservationAdminMessage(reservation, vehicle, userEmail);
      
      // Envoyer des notifications push à tous les administrateurs
      for (const admin of admins) {
        try {
          // Trouver l'utilisateur par email pour obtenir son ID
          const { userService } = await import('./userService');
          const user = await userService.findUserByEmail(admin.email);
          
          if (user && user.id) {
            console.log('Envoi de notification push à l\'administrateur:', admin.email);
            
            // Afficher la notification
            await this.showNotification(title, {
              body: `Nouvelle réservation pour ${vehicle.make} ${vehicle.model} par ${userEmail}`,
              icon: '/logo-verifleet1.png',
              data: {
                reservationId,
                vehicleId,
                type: 'new_reservation'
              }
            });
            
            // Envoyer également un email
            await this.sendNotificationEmail({
              to_email: admin.email,
              to_name: capitalizeFullName(admin.displayName || admin.email.split('@')[0]),
              subject: title,
              message: message
            });
            
            console.log('Notification envoyée avec succès à:', admin.email);
          } else {
            console.warn('Utilisateur non trouvé pour l\'email d\'administrateur:', admin.email);
          }
        } catch (adminError) {
          console.error('Erreur lors de l\'envoi de la notification à l\'administrateur:', admin.email, adminError);
          // Continuer avec les autres administrateurs
        }
      }
      
      console.log('Notifications envoyées à tous les administrateurs');
    } catch (error) {
      console.error('Erreur lors de l\'envoi des notifications aux administrateurs:', error);
      // Ne pas propager l'erreur pour ne pas bloquer le processus de réservation
    }
  }
};
